// ===== Packages =====

import React                                from 'react';
import styled                               from 'styled-components';

// ===== Constants =====

import {
    BODY_FONT_SIZE,
    READER_VIEW_PARAGRAPH_TEXT_FONT_MULTIPLIER,
    READER_VIEW_PARAGRAPH_TEXT_FONT_MULTIPLIER_SMALL,
    READER_VIEW_PERFECT_FOURTH_FONT_MULTIPLIER,
}                                           from '../../../constants/generalConstants';
import FONT_TYPE                            from '../../../constants/fontType';

// ===== Styles =====

import { basicEditorFontStyles }            from '../../../styles';

interface Props {
    isFirstElement: boolean,
    paragraphId?: string,
    plainText: string,
    showPartMarker?: boolean,
    partNumber?: number | null,
    partCount?: number | null,
    children: React.ReactNode,
    attributes: React.HTMLAttributes<HTMLElement>,
}

/**
 * Renders a standard h2 in edit mode and a reader-style Part marker block in
 * read-only presentations that opt into numbered section breaks.
 */
function HeaderTwo({
    isFirstElement = false,
    paragraphId,
    plainText,
    showPartMarker = false,
    partNumber = null,
    partCount = null,
    children,
    attributes,
}: Props): JSX.Element {
    const shouldShowPartMarker = showPartMarker && partNumber !== null;
    const normalizedTargetLabel = plainText.replace(/\s+/g, ' ').trim();

    /**
     * The numbered Part marker is decorative UI, not Slate-owned content.
     * Prevent the browser from placing the DOM selection inside those text
     * nodes so Slate never tries to resolve a point from them.
     */
    const preventPartMarkerSelection = (event: React.SyntheticEvent<HTMLDivElement>): void => {
        event.preventDefault();
    };

    if (!shouldShowPartMarker) {
        return (
            <Heading
                {...attributes}
                isFirstElement={isFirstElement}
                showPartMarker={false}
                {...(paragraphId
                    ? {
                        'data-post-link-target-id': paragraphId,
                    }
                    : {}
                )}
                {...(normalizedTargetLabel
                    ? {
                        'data-post-link-target-label': normalizedTargetLabel,
                    }
                    : {}
                )}
            >
                {children}
            </Heading>
        );
    }

    return (
        <PartContainer
            {...attributes}
            isFirstElement={isFirstElement}
            {...(paragraphId
                ? {
                    'data-post-link-target-id': paragraphId,
                }
                : {}
            )}
            {...(normalizedTargetLabel
                ? {
                    'data-post-link-target-label': normalizedTargetLabel,
                }
                : {}
            )}
        >
            <PartMarker
                aria-hidden="true"
                contentEditable={false}
                suppressContentEditableWarning
                data-testid="post-editor-header-two-part-marker"
                data-part-number={partNumber}
                data-part-count={partCount}
                onMouseDown={preventPartMarkerSelection}
                onTouchStart={preventPartMarkerSelection}
            >
                <PartNumberCard>
                    <PartNumber>
                        {partNumber}
                    </PartNumber>
                </PartNumberCard>
                {!!partCount && (
                    <PartCount>
                        {`of ${partCount}`}
                    </PartCount>
                )}
            </PartMarker>
            <Heading
                isFirstElement={isFirstElement}
                showPartMarker
            >
                {children}
            </Heading>
        </PartContainer>
    );
}

HeaderTwo.defaultProps = {
    paragraphId: undefined,
    showPartMarker: false,
    partNumber: null,
    partCount: null,
};

// ===== Styled Components =====

interface PartContainerProps {
    isFirstElement: boolean,
}

interface HeadingProps {
    isFirstElement: boolean,
    showPartMarker: boolean,
}

const Heading = styled.h2<HeadingProps>`
    ${basicEditorFontStyles}
    font-family: ${FONT_TYPE.SOURCE_SANS_3};
    margin: 0 auto;
    margin-top: ${({ isFirstElement, showPartMarker }) => {
        if (showPartMarker) {
            return '0';
        }

        return isFirstElement
            ? '0'
            : '0.5em';
    }};
    scroll-margin-top: ${`${3.5 * BODY_FONT_SIZE}px`};
    font-size: ${`${READER_VIEW_PERFECT_FOURTH_FONT_MULTIPLIER * READER_VIEW_PARAGRAPH_TEXT_FONT_MULTIPLIER}em`};
    font-weight: 700;
    color: ${({ theme }) => theme.color.textBlack};
    text-align: center;

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: ${`${READER_VIEW_PERFECT_FOURTH_FONT_MULTIPLIER * READER_VIEW_PARAGRAPH_TEXT_FONT_MULTIPLIER_SMALL}em`};
    }
`;

const PartContainer = styled.div<PartContainerProps>`
    margin-top: ${({ isFirstElement }) => (isFirstElement ? '0' : '4.5em')};
    margin-bottom: 3em;
    scroll-margin-top: ${`${3.5 * BODY_FONT_SIZE}px`};
`;

const PartMarker = styled.div`
    ${basicEditorFontStyles}
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    width: fit-content;
    max-width: 100%;
    margin: 0 auto 1.75em;

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        gap: 0.75rem;
    }
`;

const PartNumberCard = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 7rem;
    min-height: 7rem;
    padding: 0.75rem 1rem;
    background: ${({ theme }) => theme.color.cream300};
    box-shadow: ${({ theme }) => theme.color.boxShadow100};

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        min-width: 5.75rem;
        min-height: 5.75rem;
        padding: 0.625rem 0.875rem;
    }
`;

const PartNumber = styled.span`
    font-family: ${FONT_TYPE.IOWAN_OLD_STYLE};
    font-size: 5.5rem;
    line-height: 0.8;
    color: ${({ theme }) => theme.verascopeColor.white};

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: 4.25rem;
    }
`;

const PartCount = styled.span`
    display: inline-flex;
    align-items: center;
    font-family: ${FONT_TYPE.IOWAN_OLD_STYLE};
    font-size: 0.95rem;
    letter-spacing: 0.32em;
    line-height: 1;
    color: ${({ theme }) => theme.verascopeColor.gray200};
    white-space: nowrap;
    text-transform: uppercase;
    writing-mode: vertical-rl;
    transform: rotate(180deg);
    transform-origin: center;

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: 0.85rem;
        letter-spacing: 0.25em;
    }
`;

export default HeaderTwo;
